import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  GroupedModifierItem,
  ModifierItem,
  PartialModifier,
} from './useLineItemModifierIngredients.types';
import {
  capitalizeFirstLetter,
  getAddedFromModifiers,
  getDefaultDressingNames,
  getDetailsKey,
  getRemovedFromModifiers,
  getSubbedIngredientNames,
  groupByName,
} from './utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns line item added and removed ingredients derived from selected modifiers.
 */
export const useLineItemModifierIngredients = (params: Params) => {
  const { selectedModifiers = [], product, showCosts = true, showLabels = true } = params;

  const { formatMessage, formatNumber } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const defaultDressingNames = useMemo(() => getDefaultDressingNames(product), [product]);

  const subbedIngredientNames = useMemo(
    () => getSubbedIngredientNames(selectedModifiers),
    [selectedModifiers],
  );

  const addedIngredients = useMemo(
    () => getAddedFromModifiers(selectedModifiers, defaultDressingNames),
    [selectedModifiers, defaultDressingNames],
  );

  const removedIngredients = useMemo(
    () => getRemovedFromModifiers(selectedModifiers, subbedIngredientNames),
    [selectedModifiers, subbedIngredientNames],
  );

  // ─── Formatting ──────────────────────────────────────────────────────────

  const formatPortion = useCallback(
    (count: number) => {
      if (count <= 1) return '';
      if (count === 2) return formatMessage(messages.portionDouble);
      if (count === 3) return formatMessage(messages.portionTriple);

      return formatMessage(messages.portionMany, { count });
    },
    [formatMessage],
  );

  const formatGroup = useCallback(
    (group: GroupedModifierItem) => {
      const hasCost = showCosts && group.cost > 0;
      const displayGroup = { ...group, cost: hasCost ? group.cost : 0 };
      const formattedCost = formatNumber(group.cost / 100, { style: 'currency', currency: 'USD' }); // prettier-ignore

      return formatMessage(messages.ingredientEntry, {
        name: group.name,
        details: getDetailsKey(displayGroup),
        portion: formatPortion(group.count),
        cost: hasCost ? formattedCost : '',
      });
    },
    [formatMessage, formatNumber, formatPortion, showCosts],
  );

  const formatList = useCallback(
    (items: ModifierItem[]): string => groupByName(items).map(formatGroup).join(', '),
    [formatGroup],
  );

  // ─── Derived ─────────────────────────────────────────────────────────────

  const addedIngredientsText = useMemo(() => {
    if (addedIngredients.length === 0) return;

    const ingredients = formatList(addedIngredients);

    const text = showLabels
      ? formatMessage(messages.addedIngredients, { ingredients })
      : ingredients;

    return capitalizeFirstLetter(text);
  }, [addedIngredients, showLabels, formatMessage, formatList]);

  const removedIngredientsText = useMemo(() => {
    if (removedIngredients.length === 0) return;

    const ingredients = formatList(removedIngredients);

    const text = showLabels
      ? formatMessage(messages.removedIngredients, { ingredients })
      : ingredients;

    return capitalizeFirstLetter(text);
  }, [removedIngredients, showLabels, formatMessage, formatList]);

  return {
    addedIngredients,
    addedIngredientsText,
    removedIngredients,
    removedIngredientsText,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addedIngredients: {
    defaultMessage: 'Add {ingredients}',
    description: 'Line item | Modifier ingredients | Added ingredients',
  },
  removedIngredients: {
    defaultMessage: 'Remove {ingredients}',
    description: 'Line item | Modifier ingredients | Removed ingredients',
  },
  ingredientEntry: {
    defaultMessage:
      '{name}{details, select, portionAndCost { ({portion}, +{cost})} portionOnly { ({portion})} costOnly { (+{cost})} other {}}',
    description:
      'Line item | Modifier ingredients | Single ingredient with optional portion and cost',
  },
  portionDouble: {
    defaultMessage: 'double',
    description: 'Line item | Modifier ingredients | Portion label for two portions',
  },
  portionTriple: {
    defaultMessage: 'triple',
    description: 'Line item | Modifier ingredients | Portion label for three portions',
  },
  portionMany: {
    defaultMessage: '{count} portions',
    description: 'Line item | Modifier ingredients | Portion label for four or more portions',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  selectedModifiers?: ReadonlyArray<PartialModifier>;
  showCosts?: boolean;
  showLabels?: boolean;
  product: { ingredients?: readonly { name?: string; kind?: string | null }[] };
};
